export const HELP_CATEGORIES = [
  { id: "all", label: "All Topics" },
  { id: "getting-started", label: "Getting Started" },
  { id: "assistant", label: "AI Assistant" },
  { id: "benchmarking", label: "Benchmarking" },
  { id: "library", label: "My Library & Exports" },
  { id: "entities", label: "Faculties & Entities" },
  { id: "account", label: "Account & Settings" },
];

export const QUICK_GUIDES = [
  {
    id: "guide-assistant",
    title: "AI Research Assistant",
    description: "Leverage natural language queries to synthesize literature, evaluate academic impact, and brainstorm grant ideas.",
    category: "assistant",
    tag: "Core Feature",
    link: "/explore/assistant",
    actionLabel: "Open Assistant",
  },
  {
    id: "guide-benchmarking",
    title: "Benchmarking & Peer Analysis",
    description: "Compare universities, departments, and individual researchers across common citation metrics and thematic keywords.",
    category: "benchmarking",
    tag: "Analytics",
    link: "/explore/analysis",
    actionLabel: "Explore Analysis",
  },
  {
    id: "guide-library",
    title: "My Library & Export Center",
    description: "Track background export jobs, download CSV/Excel reports, and organize your favorite research portfolios.",
    category: "library",
    tag: "Productivity",
    link: "/explore/my-library",
    actionLabel: "View Library",
  },
  {
    id: "guide-entities",
    title: "Entity Explorer",
    description: "Deep dive into profiles of institutions, faculty rosters, units, and academic journals across global indices.",
    category: "entities",
    tag: "Directory",
    link: "/explore/faculties",
    actionLabel: "Explore Faculties",
  },
];

export const FAQS = [
  {
    id: "faq-1",
    category: "getting-started",
    question: "What is Academic Online and how does it organize research data?",
    answer:
      "Academic Online is an institutional intelligence and analytics platform. It aggregates millions of peer-reviewed publications, author profiles, and institutional hierarchies into unified metrics, real-time citation analysis, and AI-assisted insights.",
    tags: ["overview", "data", "platform"],
  },
  {
    id: "faq-2",
    category: "getting-started",
    question: "How often is the publication and citation index updated?",
    answer:
      "Global citation indexes, publication logs, and institutional affiliations are refreshed on a scheduled bi-weekly ingestion cycle. Real-time metrics such as assistant queries and customized benchmarking views reflect the latest database snapshot immediately.",
    tags: ["updates", "refresh", "index", "data"],
  },
  {
    id: "faq-3",
    category: "assistant",
    question: "What types of questions can I ask the AI Research Assistant?",
    answer:
      "You can ask natural language questions such as: 'Summarize top research themes for Stanford CS department over the last 5 years', 'Compare citation impact between Faculty A and Faculty B in Bioinformatics', or 'Find emergent topics in renewable energy storage'.",
    tags: ["assistant", "ai", "queries", "prompts"],
  },
  {
    id: "faq-4",
    category: "assistant",
    question: "Are AI Assistant query outputs exportable?",
    answer:
      "Yes. Insights and generated summaries from the AI Assistant can be copied, saved to your notes, or exported alongside citation graphs into My Library.",
    tags: ["export", "assistant", "notes"],
  },
  {
    id: "faq-5",
    category: "benchmarking",
    question: "How are Field-Weighted Citation Impact and Percentiles calculated?",
    answer:
      "We normalize citation counts against the global median of papers published in the same subject category and publication year. This ensures fair comparisons between fast-publishing fields (e.g., Biomedicine) and slower-publishing disciplines (e.g., Mathematics).",
    tags: ["citations", "percentiles", "calculation", "metrics"],
  },
  {
    id: "faq-6",
    category: "benchmarking",
    question: "Can I save custom peer groups for recurring benchmarking?",
    answer:
      "Yes. Navigate to Benchmarking > Peer, select your benchmark institution, choose peer comparators, and click 'Save Benchmark Group'. You can return to this preset at any time.",
    tags: ["peer", "comparison", "save", "presets"],
  },
  {
    id: "faq-7",
    category: "library",
    question: "Where can I download my generated export reports?",
    answer:
      "Go to 'My Library' from the left sidebar. The Export Center tab tracks all running, completed, and archived export jobs. You can directly download XLSX, CSV, or PDF files from there.",
    tags: ["library", "export", "download", "reports"],
  },
  {
    id: "faq-8",
    category: "library",
    question: "Why does my export job remain in 'Processing' status?",
    answer:
      "Comprehensive reports involving thousands of author publications or multi-decade citation histories run through asynchronous worker queues. Most jobs finish in under 30 seconds. A red badge on the My Library sidebar icon will notify you the moment your export is ready.",
    tags: ["processing", "background job", "export", "status"],
  },
  {
    id: "faq-9",
    category: "entities",
    question: "How do I discover faculty collaboration networks?",
    answer:
      "Visit any Faculty or Institution detail page and switch to the 'Collaborations' tab or navigate to Admin Tools > Collaboration to inspect geographic co-authorship heatmaps and institutional link diagrams.",
    tags: ["collaboration", "faculty", "co-authorship", "network"],
  },
  {
    id: "faq-10",
    category: "account",
    question: "How do I update my profile details or profile picture?",
    answer:
      "Click on your profile avatar in the bottom-left corner of the sidebar, select 'Settings', and navigate to the 'Profile' tab. You can update your full name and upload a new avatar (JPEG, PNG, WebP up to 2MB).",
    tags: ["settings", "profile", "avatar", "name"],
  },
  {
    id: "faq-11",
    category: "account",
    question: "How do I secure my account and sign out of other active sessions?",
    answer:
      "Under Settings > Account & Plan, in the 'Security & Sessions' card, click 'Sign Out All Other Sessions'. This will immediately invalidate tokens on any other browser or device.",
    tags: ["security", "sessions", "revoke", "devices"],
  },
];

export const SUPPORT_CATEGORIES = [
  { value: "technical", label: "Technical Issue / Error" },
  { value: "bug", label: "Report a Bug" },
  { value: "feature", label: "Feature Request / Suggestion" },
  { value: "data", label: "Data Inaccuracy / Discrepancy" },
  { value: "billing", label: "Account & Subscription Plan" },
  { value: "general", label: "General Inquiry" },
];

export const PRIORITY_OPTIONS = [
  { value: "low", label: "Low - General question / Non-urgent" },
  { value: "medium", label: "Medium - Standard issue" },
  { value: "high", label: "High - Feature impaired" },
  { value: "urgent", label: "Urgent - Work completely blocked" },
];
